import { useCallback, useEffect, useRef, useState } from 'react'
import { Copy, Loader2, MoreHorizontal, Pin, PinOff, Trash2 } from 'lucide-react'

import {
  useArtifactCanvasHeaderActions,
  useArtifactCanvasHeaderTitle,
  useArtifactCanvasHeaderTitleAddon,
} from '@/components/artifact/artifact-canvas-header-slot'
import { ArtifactCanvasBody } from '@/components/artifact/artifact-canvas-layout'
import { ListViewSwitcher } from '@/components/filters/list-view-switcher'
import { PageHeaderActionTertiary } from '@/components/navigation/page-header-actions'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import {
  useDeleteDocument,
  useDocument,
  useDuplicateDocument,
  usePinDocument,
  useUnpinDocument,
  useUpdateDocument,
} from '@/hooks/use-documents'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import type { RichTextEditorMode } from '@/components/ui/rich-text-shared'
import { RICH_TEXT_MODE_OPTIONS } from '@/lib/rich-text-mode-options'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { displayDocumentTitle } from './documents-shared'

const AUTOSAVE_DEBOUNCE_MS = 800

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

function focusDocumentBody(documentId: string) {
  requestAnimationFrame(() => {
    document.getElementById(`document-body-${documentId}`)?.focus()
  })
}

export type DocumentCanvasProps = {
  documentId: string
  onClose: () => void
  onDeleted?: () => void
  onDuplicated?: (newDocumentId: string) => void
}

export function DocumentCanvas({
  documentId,
  onClose,
  onDeleted,
  onDuplicated,
}: DocumentCanvasProps) {
  const { data: document, isLoading, isFetching, error } = useDocument(documentId)
  const updateDocument = useUpdateDocument(documentId)
  const deleteDocument = useDeleteDocument()
  const duplicateDocument = useDuplicateDocument()
  const pinDocument = usePinDocument()
  const unpinDocument = useUnpinDocument()

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [hydrated, setHydrated] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [editorMode, setEditorMode] = useState<RichTextEditorMode>('preview')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const lastSavedRef = useRef({ title: '', body: '' })
  const hydratedDocIdRef = useRef<string | null>(null)
  const titleRef = useRef(title)
  const bodyRef = useRef(body)
  const pendingPayloadRef = useRef<{ title?: string; body?: string } | null>(null)

  titleRef.current = title
  bodyRef.current = body

  const debouncedTitle = useDebouncedValue(title, AUTOSAVE_DEBOUNCE_MS)
  const debouncedBody = useDebouncedValue(body, AUTOSAVE_DEBOUNCE_MS)

  const saveChanges = useCallback(
    async (payload: { title?: string; body?: string }) => {
      const saved = await updateDocument.mutateAsync(payload)
      lastSavedRef.current = { title: saved.title, body: saved.body }
      return saved
    },
    [updateDocument],
  )

  const flushPendingChanges = useCallback(async () => {
    if (!hydratedDocIdRef.current) return

    const nextTitle = titleRef.current
    const nextBody = bodyRef.current
    const titleDirty = nextTitle !== lastSavedRef.current.title
    const bodyDirty = nextBody !== lastSavedRef.current.body
    if (!titleDirty && !bodyDirty) return

    const payload: { title?: string; body?: string } = {}
    if (titleDirty) payload.title = nextTitle
    if (bodyDirty) payload.body = nextBody
    await saveChanges(payload)
  }, [saveChanges])

  const flushPendingChangesRef = useRef(flushPendingChanges)
  flushPendingChangesRef.current = flushPendingChanges

  useEffect(() => {
    hydratedDocIdRef.current = null
    setHydrated(false)
    lastSavedRef.current = { title: '', body: '' }
    setTitle('')
    setBody('')
    setSaveStatus('idle')
    setEditorMode('preview')
  }, [documentId])

  useEffect(() => {
    if (!document || isFetching) return
    if (hydratedDocIdRef.current === documentId) return

    setTitle(document.title)
    setBody(document.body)
    lastSavedRef.current = { title: document.title, body: document.body }
    hydratedDocIdRef.current = documentId
    setHydrated(true)
  }, [document, documentId, isFetching])

  useEffect(() => {
    if (!hydrated) return
    const titleChanged = debouncedTitle !== lastSavedRef.current.title
    const bodyChanged = debouncedBody !== lastSavedRef.current.body
    if (!titleChanged && !bodyChanged) return

    const payload: { title?: string; body?: string } = {}
    if (titleChanged) payload.title = debouncedTitle
    if (bodyChanged) payload.body = debouncedBody
    pendingPayloadRef.current = payload
    setSaveStatus('pending')
  }, [debouncedTitle, debouncedBody, hydrated])

  useEffect(() => {
    if (saveStatus !== 'pending' || !pendingPayloadRef.current) return

    const payload = pendingPayloadRef.current
    pendingPayloadRef.current = null
    setSaveStatus('saving')

    void saveChanges(payload)
      .then(() => setSaveStatus('saved'))
      .catch(() => setSaveStatus('error'))
  }, [saveStatus, saveChanges])

  useEffect(() => {
    return () => {
      void flushPendingChangesRef.current()
    }
  }, [documentId])

  const handleTogglePin = async () => {
    if (!document) return
    if (document.is_pinned) {
      await unpinDocument.mutateAsync(document.id)
    } else {
      await pinDocument.mutateAsync(document.id)
    }
  }

  const handleDuplicate = async () => {
    if (!document) return
    await flushPendingChanges()
    const copy = await duplicateDocument.mutateAsync(document.id)
    onDuplicated?.(copy.id)
  }

  const saveLabel =
    saveStatus === 'saving' || saveStatus === 'pending'
      ? 'Saving…'
      : saveStatus === 'saved'
        ? 'Saved'
        : saveStatus === 'error'
          ? 'Save failed'
          : ''

  const isPinPending = pinDocument.isPending || unpinDocument.isPending
  const isMenuBusy = duplicateDocument.isPending || deleteDocument.isPending

  // Shell title is set once from list titleHint; on refresh the list may not be
  // ready yet so the header would stick on "Untitled". Push the live title after hydrate.
  useArtifactCanvasHeaderTitle(hydrated ? displayDocumentTitle(title) : null)

  const documentTitleAddon =
    document && hydrated ? (
      <PageHeaderActionTertiary
        aria-label={document.is_pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
        aria-pressed={document.is_pinned}
        title={document.is_pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
        disabled={isPinPending}
        pressFeedback
        onClick={() => void handleTogglePin()}
        className={document.is_pinned ? 'text-foreground' : undefined}
      >
        {document.is_pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
      </PageHeaderActionTertiary>
    ) : null

  useArtifactCanvasHeaderTitleAddon(documentTitleAddon)

  const documentHeaderActions =
    document && hydrated ? (
      <div className="flex items-center gap-2">
        <ListViewSwitcher
          value={editorMode}
          onValueChange={setEditorMode}
          options={RICH_TEXT_MODE_OPTIONS}
          ariaLabel="Editor mode"
          variant="icon"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <PageHeaderActionTertiary
              aria-label="Document actions"
              disabled={isMenuBusy}
              pressFeedback
            >
              <MoreHorizontal className="size-4" />
            </PageHeaderActionTertiary>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => void handleDuplicate()}
              disabled={duplicateDocument.isPending}
            >
              <Copy data-icon="inline-start" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 data-icon="inline-start" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ) : null

  useArtifactCanvasHeaderActions(documentHeaderActions)

  if (error) {
    return (
      <ArtifactCanvasBody className="flex min-h-0 flex-1 flex-col space-y-0 p-6">
        <p className="text-sm text-destructive" role="alert">
          {error instanceof Error ? error.message : 'Document not found.'}
        </p>
      </ArtifactCanvasBody>
    )
  }

  if (isLoading || !document || !hydrated) {
    return (
      <ArtifactCanvasBody className="flex min-h-0 flex-1 flex-col items-center justify-center space-y-0 p-6">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading document…</p>
      </ArtifactCanvasBody>
    )
  }

  return (
    <ArtifactCanvasBody className="flex min-h-0 flex-1 flex-col space-y-0 p-0">
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={(event) => event.preventDefault()}>
        <div className="flex shrink-0 items-start gap-3 px-6 pt-6 pb-2">
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                focusDocumentBody(document.id)
              }
            }}
            placeholder="Untitled"
            aria-label="Document title"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-2xl font-semibold tracking-tight text-foreground shadow-none outline-none placeholder:italic placeholder:text-muted-foreground focus-visible:ring-0 sm:text-3xl"
          />
          {saveLabel ? (
            <span className="shrink-0 pt-1 text-xs text-muted-foreground" role="status">
              {saveLabel}
            </span>
          ) : null}
        </div>

        <RichTextEditor
          key={document.id}
          id={`document-body-${document.id}`}
          value={body}
          onChange={setBody}
          frame="canvas"
          mode={editorMode}
          onModeChange={setEditorMode}
          modeSwitcher="none"
          placeholder="Start writing…"
          className="min-h-0 flex-1"
        />
      </form>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{displayDocumentTitle(title)}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the page for everyone on Yes Human. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                void flushPendingChanges()
                  .then(() => deleteDocument.mutateAsync(document.id))
                  .then(() => {
                    onDeleted?.()
                    onClose()
                  })
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ArtifactCanvasBody>
  )
}
